import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { api, fieldErrorsFromDetail } from '../lib/api'
import ConfirmDialog from './ConfirmDialog'

// The invitation, edited in place the way you would write an email. The
// subject and body are contentEditable; the parts that fill in for each
// person — their name, the template's variables, the engagement's facts, the
// personal link — render as highlighted chips that can be deleted but not
// typed into, and read back as their {PLACEHOLDER} when the text is saved.
// Clicking a name on the left repaints the chips with that person's values,
// so "What Marcus will receive" is exactly that.
//
// Edits autosave to the space's draft (PATCH /spaces/{id}/invite), which the
// server also copies onto every link added but not yet sent, so one edit
// reaches all of them. "Revert to original" is the admin's wording for this
// space (Settings), not the template's. {LINK} is required by the server; if the chip is
// deleted, it is put back at the end on save — the footnote says so.

const TOKEN_RE = /(\{[A-Za-z_][A-Za-z0-9_]*\})/

// The marker for {LINK}, compared by identity: every string has a legacy
// `.link()` method, so a property check would call every name a link.
const LINK = Symbol('link')

// "October 1, 2026 at 11:59 p.m. (Toronto time)" for a date-only end date —
// the same wording the send renders into {EXPIRY_DATE}. Built from the parts
// so the day never slips through a UTC parse.
function expiryText(endsAt) {
  if (!endsAt) return '[expiry date]'
  const [year, month, day] = String(endsAt).slice(0, 10).split('-').map(Number)
  const long = new Date(year, month - 1, day).toLocaleDateString('en-CA', { dateStyle: 'long' })
  return `${long} at 11:59 p.m. (Toronto time)`
}

function escapeHtml(text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// What each placeholder shows for this person. Null means "not ours" — the
// text stays as typed, editable, the way the server leaves an unknown one.
function resolver(space, person) {
  const variables = space.template_variables ?? []
  const labels = Object.fromEntries(variables.map((v) => [v.key, v.label]))
  return (name) => {
    const key = name.toLowerCase()
    switch (key) {
      case 'first_name':
        return person?.first_name ?? '[First name]'
      case 'last_name':
        return person?.last_name ?? '[Last name]'
      case 'principal_name':
        return space.principal_name
      case 'principal_title':
        return space.principal_title ?? ''
      case 'client_name':
        return space.client_name
      case 'purpose':
        return space.purpose ?? ''
      case 'engagement':
        return space.engagement
      case 'expiry_date':
        return expiryText(space.ends_at)
      case 'link':
        return LINK
      default:
        if (key in labels) return person?.variables?.[key] || `[${labels[key]}]`
        return null
    }
  }
}

function lineToHtml(text, resolve) {
  return text
    .split(TOKEN_RE)
    .filter((part) => part !== '')
    .map((part) => {
      const match = part.match(/^\{([A-Za-z_][A-Za-z0-9_]*)\}$/)
      if (!match) return escapeHtml(part)
      const value = resolve(match[1])
      if (value === null) return escapeHtml(part)
      const token = escapeHtml(part)
      if (value === LINK) {
        return `<span class="tok tok--link" data-token="${token}" contenteditable="false">Start the conversation</span>`
      }
      return `<span class="tok" data-token="${token}" contenteditable="false">${escapeHtml(value)}</span>`
    })
    .join('')
}

function bodyToHtml(text, resolve) {
  return text
    .split(/\n\n/)
    .map((paragraph) => {
      const inner = paragraph.split('\n').map((line) => lineToHtml(line, resolve)).join('<br>')
      return `<p>${inner || '<br>'}</p>`
    })
    .join('')
}

// The DOM back to text with {PLACEHOLDER}s. Browsers wrap new lines in <div>
// or <p> as they please; both count as a paragraph break, <br> as a line.
function walk(node) {
  if (node.nodeType === 3) return node.nodeValue
  if (node.nodeType !== 1) return ''
  if (node.dataset?.token) return node.dataset.token
  if (node.tagName === 'BR') return '\n'
  return Array.from(node.childNodes).map(walk).join('')
}

function subjectFromDom(element) {
  return walk(element).replace(/\n/g, ' ')
}

function bodyFromDom(element) {
  const blocks = []
  let loose = ''
  for (const node of Array.from(element.childNodes)) {
    const isBlock = node.nodeType === 1 && (node.tagName === 'P' || node.tagName === 'DIV')
    if (isBlock) {
      if (loose) {
        blocks.push(loose)
        loose = ''
      }
      blocks.push(walk(node).replace(/\n$/, ''))
    } else {
      loose += walk(node)
    }
  }
  if (loose) blocks.push(loose)
  return blocks.join('\n\n').replace(/\n{3,}/g, '\n\n')
}

export default function InviteComposer({
  space,
  person,
  live,
  unsentCount,
  onSaved,
  onEditingChange = null, // (true while an edit is unsaved) — the page's Send waits on it
}) {
  const subjectRef = useRef(null)
  const bodyRef = useRef(null)
  // The text with placeholders, as the server holds it. Only `saved` is
  // mirrored from the space; `draft` is what is being typed.
  const [draft, setDraft] = useState({ subject: space.email_subject, body: space.email_body })
  const [dirty, setDirty] = useState(false)
  const [edited, setEdited] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [paintKey, setPaintKey] = useState(0)
  const [confirmRevert, setConfirmRevert] = useState(false)
  const [reverting, setReverting] = useState(false)
  // What is being typed right now, readable from inside a save that started
  // earlier: a save must not mark the text clean if it changed meanwhile.
  const latest = useRef(draft)
  latest.current = draft
  const resolve = resolver(space, person)

  // Paint the chips for this person. Never on a keystroke — that would move
  // the caret — only when the person changes, or after a save that put
  // {LINK} back.
  useLayoutEffect(() => {
    if (subjectRef.current) subjectRef.current.innerHTML = lineToHtml(draft.subject, resolve)
    if (bodyRef.current) bodyRef.current.innerHTML = bodyToHtml(draft.body, resolve)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.link_id ?? person?.participant_id ?? null, paintKey])

  const save = useCallback(async () => {
    let body = draft.body
    let restored = false
    if (!body.includes('{LINK}')) {
      body = `${body.replace(/\s+$/, '')}\n\n{LINK}`
      restored = true
    }
    const subject = draft.subject.trim()
    if (!subject || !body.trim()) {
      setError(subject ? 'The email needs some text.' : 'The email needs a subject.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const updated = await api(`/api/v1/spaces/${space.id}/invite`, {
        method: 'PATCH',
        body: { email_subject: subject, email_body: body },
      })
      // Typing continued while this was in flight: leave it dirty so the next
      // pause saves the newer text, and never repaint over the caret.
      const stale = latest.current !== draft
      if (!stale) setDirty(false)
      onSaved(updated)
      if (restored && !stale) {
        setDraft({ subject, body })
        setPaintKey((key) => key + 1)
      }
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      setError(
        (mapped && (mapped.email_body || mapped.email_subject)) ||
          err.detail ||
          'Could not save your edit — it is still here, try again.',
      )
    } finally {
      setSaving(false)
    }
  }, [draft, space.id, onSaved])

  // Debounced autosave, re-armed on every keystroke.
  useEffect(() => {
    if (!dirty) return undefined
    const timer = setTimeout(save, 1000)
    return () => clearTimeout(timer)
  }, [dirty, draft, save])

  // The Send button lives on the page, above this; it must not fire while an
  // edit is still on its way to the server.
  useEffect(() => {
    onEditingChange?.(dirty || saving)
  }, [dirty, saving, onEditingChange])

  // Leaving mid-debounce would drop the edit silently — let the browser ask.
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // Back to the template's wording, for everyone not yet sent. The server
  // does the copy (POST …/invite/reset); the chips are repainted from it.
  async function revert() {
    setReverting(true)
    setError(null)
    try {
      const updated = await api(`/api/v1/spaces/${space.id}/invite/reset`, { method: 'POST' })
      setDraft({ subject: updated.email_subject, body: updated.email_body })
      setDirty(false)
      setEdited(false)
      onSaved(updated)
      setPaintKey((key) => key + 1)
    } catch (err) {
      setError(err.detail || 'Could not revert the invitation. Please try again.')
    } finally {
      setReverting(false)
      setConfirmRevert(false)
    }
  }

  // Read the DOM in the handler itself: React clears the event's
  // currentTarget once the handler returns, and a state updater runs later.
  function handleSubjectInput(event) {
    const subject = subjectFromDom(event.currentTarget)
    setDraft((current) => ({ ...current, subject }))
    setDirty(true)
    setEdited(true)
  }

  function handleBodyInput(event) {
    const body = bodyFromDom(event.currentTarget)
    setDraft((current) => ({ ...current, body }))
    setDirty(true)
    setEdited(true)
  }

  // Pasted text comes in plain, so a paragraph from Outlook doesn't bring
  // its fonts along.
  function handlePaste(event) {
    event.preventDefault()
    const text = event.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  function handleSubjectKey(event) {
    if (event.key === 'Enter') event.preventDefault()
  }

  const first = person ? person.first_name : null
  const to = person ? `${person.first_name} ${person.last_name} <${person.email}>` : '[each person]'
  const note = saving
    ? 'Saving…'
    : error
      ? error
      : !live
        ? 'This engagement has ended'
        : edited
          ? `Edited · applies to ${unsentCount === 1 ? 'the 1 not yet sent' : `all ${unsentCount} not yet sent`}`
          : 'Click the text to edit'

  return (
    <section className="compose" aria-labelledby="compose-title">
      <div className="compose__head">
        <p id="compose-title" className="eyebrow-sm">
          {first ? `What ${first} will receive` : 'What each person will receive'}
        </p>
        <span className="compose__tools">
          <span
            className={`compose__note${error ? ' compose__note--error' : edited && !saving ? ' compose__note--ok' : ''}`}
            role="status"
          >
            {note}
          </span>
          {live && (
            <button
              type="button"
              className="text-btn compose__revert"
              onClick={() => setConfirmRevert(true)}
              disabled={reverting || saving}
            >
              Revert to original
            </button>
          )}
        </span>
      </div>

      {confirmRevert && (
        <ConfirmDialog label="Revert the invitation?" onClose={() => setConfirmRevert(false)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Revert the invitation?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            The subject and body go back to the wording Capston set up for this engagement, for
            everyone not yet sent. Anyone already sent keeps the email they got. Your edits here
            are lost.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setConfirmRevert(false)}
              disabled={reverting}
            >
              Keep my wording
            </button>
            <button type="button" className="btn btn--dark" onClick={revert} disabled={reverting}>
              {reverting ? 'Reverting…' : 'Revert'}
            </button>
          </div>
        </ConfirmDialog>
      )}

      <div className="mail">
        <div className="mail__head">
          <p>
            <strong>From:</strong> {space.sender_name}
          </p>
          <p>
            <strong>To:</strong> {to}
          </p>
          <p>
            <strong>Reply-To:</strong> {space.principal_email}
          </p>
        </div>
        <div className="mail__subject-row">
          <span className="mail__label">Subject</span>
          <div
            ref={subjectRef}
            className="mail__subject"
            contentEditable={live}
            suppressContentEditableWarning
            role="textbox"
            aria-label="Subject"
            onInput={handleSubjectInput}
            onKeyDown={handleSubjectKey}
            onPaste={handlePaste}
          />
        </div>
        <div
          ref={bodyRef}
          className="mail__body"
          contentEditable={live}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Body"
          onInput={handleBodyInput}
          onPaste={handlePaste}
        />
      </div>
      <p className="compose__foot">
        Highlighted words change for each person. The button is their personal link — if it
        goes missing, it is added back at the end.
      </p>
    </section>
  )
}
